import { onMounted, onUnmounted, ref, Ref } from "vue";
import { getSocket } from "@/utils/socket";
import type { Socket } from "socket.io-client";
import type {
  EmailReceivedPayload,
  EmailOpenedPayload,
  EmailLinkClickedPayload,
} from "@/dtos/EmailWebhook.dto";
import { belongsToAccount } from "@/utils/emailWebhookHandlers";

/**
 * Options for the useEmailWebhooks composable
 */
export interface UseEmailWebhooksOptions {
  /**
   * Current account ID to filter emails
   */
  accountId?: string;

  /**
   * Whether the email channel is currently selected
   */
  isEmailChannelSelected?: boolean;

  /**
   * Callback for handling email received events
   * @param payload - The email received payload
   * @param helpers - Helper functions for email handling
   */
  onEmailReceived?: (
    payload: EmailReceivedPayload["payload"],
    helpers: {
      belongsToAccount: (payload: { accountId?: string }) => boolean;
      createEmailFromPayload: (payload: EmailReceivedPayload["payload"]) => any;
      checkEmailExists: (newEmail: any, emails: any[]) => boolean;
    }
  ) => void;

  /**
   * Callback for handling email opened events
   * @param payload - The email opened payload
   * @param helpers - Helper functions for email handling
   */
  onEmailOpened?: (
    payload: EmailOpenedPayload["payload"],
    helpers: {
      belongsToAccount: (payload: { accountId?: string }) => boolean;
      findEmailInList: (emails: any[], emailId: string) => { email: any; index: number } | null;
      updateEmailReadStatus: (emails: any[], emailId: string, readAt: string | null) => boolean;
    }
  ) => void;

  /**
   * Callback for handling email link clicked events
   * @param payload - The email link clicked payload
   * @param helpers - Helper functions for email handling
   */
  onEmailLinkClicked?: (
    payload: EmailLinkClickedPayload["payload"],
    helpers: {
      belongsToAccount: (payload: { accountId?: string }) => boolean;
    }
  ) => void;

  /**
   * Whether to show toast notifications (default: true)
   */
  showNotifications?: boolean;

  /**
   * Maximum number of processed event IDs to keep in memory (default: 1000)
   */
  maxEventIds?: number;
}

/**
 * Creates a standardized email object from payload
 */
export function createEmailFromPayload(payload: EmailReceivedPayload["payload"]): any {
  return {
    ...payload.metadata,
    id: payload.emailId,
    emailId: payload.emailId,
    provider_id: payload.emailId,
    senderEmail: payload.senderEmail,
    subject: payload.subject || "(No Subject)",
    date: payload.eventTimestamp,
    timestamp: payload.eventTimestamp,
    read_date: null,
    isRead: false,
    contactId: payload.contactId,
    accountId: payload.accountId,
    from_attendee: payload.metadata?.from_attendee,
    to_attendees: payload.metadata?.to_attendees,
    body_preview: payload.metadata?.body_preview,
  };
}

/**
 * Checks if an email already exists in the emails array
 */
export function checkEmailExists(newEmail: any, emails: any[]): boolean {
  return emails.some(
    (e) =>
      (e.id && e.id === newEmail.id) ||
      (e.emailId && e.emailId === newEmail.emailId) ||
      (e.provider_id && e.provider_id === newEmail.provider_id)
  );
}

/**
 * Find email in list by ID (checks both id and provider_id)
 */
export function findEmailInList(
  emails: any[],
  emailId: string
): { email: any; index: number } | null {
  const index = emails.findIndex((email) => email.id === emailId || email.provider_id === emailId);
  if (index === -1) return null;
  return { email: emails[index], index };
}

/**
 * Update email read status in list
 */
export function updateEmailReadStatus(
  emails: any[],
  emailId: string,
  readAt: string | null
): boolean {
  const result = findEmailInList(emails, emailId);
  if (result) {
    result.email.read_date = readAt;
    return true;
  }
  return false;
}

/**
 * Composable for handling email webhook events via Socket.IO
 * Provides real-time email event handling with deduplication
 * @param options - Configuration options for the composable
 * @returns Object containing connection state and control functions
 */
export function useEmailWebhooks(options: UseEmailWebhooksOptions = {}) {
  const {
    accountId,
    isEmailChannelSelected = false,
    onEmailReceived,
    onEmailOpened,
    onEmailLinkClicked,
    maxEventIds = 1000,
  } = options;

  const socket: Ref<Socket | null> = ref(null);
  const isConnected: Ref<boolean> = ref(false);
  const processedEmailEventIds: Ref<Set<string>> = ref(new Set<string>());

  /**
   * Check if event has already been processed
   */
  const isEventProcessed = (eventId: string | null): boolean => {
    if (!eventId) return false;
    return processedEmailEventIds.value.has(eventId);
  };

  /**
   * Mark event as processed
   */
  const markEventProcessed = (eventId: string | null): void => {
    if (!eventId) return;
    processedEmailEventIds.value.add(eventId);

    // Cleanup old event IDs if we exceed the limit
    if (processedEmailEventIds.value.size > maxEventIds) {
      const firstId = processedEmailEventIds.value.values().next().value;
      if (firstId) {
        processedEmailEventIds.value.delete(firstId);
      }
    }
  };

  /**
   * Check if email belongs to current account
   */
  const checkBelongsToAccount = (payload: { accountId?: string }): boolean => {
    return belongsToAccount(payload, accountId);
  };

  /**
   * Handle email received event
   */
  const handleEmailReceived = (data: EmailReceivedPayload) => {
    try {
      const { payload } = data;
      if (!payload) return;

      const eventId = payload.eventId;
      if (!eventId) return;

      // Skip if already processed (deduplication)
      if (isEventProcessed(eventId)) {
        console.debug("Skipping duplicate email received event:", eventId);
        return;
      }

      markEventProcessed(eventId);

      // Call user-provided callback
      if (onEmailReceived) {
        onEmailReceived(payload, {
          belongsToAccount: checkBelongsToAccount,
          createEmailFromPayload,
          checkEmailExists,
        });
      }
    } catch (error) {
      console.error("Error handling email received event:", error);
    }
  };

  /**
   * Handle email opened event
   */
  const handleEmailOpened = (data: EmailOpenedPayload) => {
    try {
      const { payload } = data;
      if (!payload) return;

      const eventId = payload.eventId;
      if (!eventId) return;

      // Skip if already processed
      if (isEventProcessed(eventId)) {
        console.debug("Skipping duplicate email opened event:", eventId);
        return;
      }

      markEventProcessed(eventId);

      // Call user-provided callback
      if (onEmailOpened) {
        onEmailOpened(payload, {
          belongsToAccount: checkBelongsToAccount,
          findEmailInList,
          updateEmailReadStatus,
        });
      }
    } catch (error) {
      console.error("Error handling email opened event:", error);
    }
  };

  /**
   * Handle email link clicked event
   */
  const handleEmailLinkClicked = (data: EmailLinkClickedPayload) => {
    try {
      const { payload } = data;
      if (!payload) return;

      const eventId = payload.eventId;
      if (!eventId) return;

      // Skip if already processed
      if (isEventProcessed(eventId)) {
        console.debug("Skipping duplicate email link clicked event:", eventId);
        return;
      }

      markEventProcessed(eventId);

      // Call user-provided callback
      if (onEmailLinkClicked) {
        onEmailLinkClicked(payload, {
          belongsToAccount: checkBelongsToAccount,
        });
      }
    } catch (error) {
      console.error("Error handling email link clicked event:", error);
    }
  };

  /**
   * Setup socket connection and event listeners
   */
  const setupSocket = () => {
    try {
      socket.value = getSocket();
      isConnected.value = socket.value.connected;

      // Connection handlers
      socket.value.on("connect", () => {
        isConnected.value = true;
      });

      socket.value.on("disconnect", () => {
        isConnected.value = false;
      });

      socket.value.on("connect_error", (error) => {
        isConnected.value = false;
        console.error("❌ Email webhooks: Socket.IO connection error:", error);
      });

      // Email event handlers
      socket.value.on("unipile:email:received", handleEmailReceived);
      socket.value.on("unipile:email:opened", handleEmailOpened);
      socket.value.on("unipile:email:link_clicked", handleEmailLinkClicked);
    } catch (error) {
      console.error("Failed to setup email webhooks socket:", error);
      isConnected.value = false;
    }
  };

  /**
   * Cleanup socket connection and event listeners
   */
  const cleanupSocket = () => {
    if (socket.value) {
      try {
        socket.value.off("unipile:email:received");
        socket.value.off("unipile:email:opened");
        socket.value.off("unipile:email:link_clicked");
        socket.value.off("connect");
        socket.value.off("disconnect");
        socket.value.off("connect_error");
      } catch (error) {
        console.error("Error cleaning up email webhooks socket:", error);
      }
    }
  };

  // Auto-setup socket in Composition API; for Options API, call setupSocket() manually.
  try {
    onMounted(() => {
      setupSocket();
    });

    onUnmounted(() => {
      cleanupSocket();
    });
  } catch (e) {
    // Not in Composition API context, manual setup required
    // Components using Options API should call setupSocket() manually
  }

  return {
    socket,
    isConnected,
    processedEmailEventIds,
    setupSocket,
    cleanupSocket,
    createEmailFromPayload,
    checkEmailExists,
    findEmailInList,
    updateEmailReadStatus,
    belongsToAccount: checkBelongsToAccount,
  };
}
